import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Users,
  Search,
  Eye,
  DollarSign,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  UserCheck,
  Award,
  Calendar,
  Shield,
  Loader2,
  X,
  CircleX,
  Power,
  Download,
  Mail,
  CheckCircle,
} from "lucide-react";
import { toast } from "react-toastify";
import ViewPromoterModal from "../../components/ViewPromoterModal";
import ActivationConfirmationModal from "../../components/ActivationConfirmationModal";
import Navbar from "../../components/Navbar";
import affiliateService from "../../requests/admin/AffiliateService";

const Promoters = () => {
  const navigate = useNavigate();
  const [promoters, setPromoters] = useState([]);
  // Separate input state from actual search term to enable debouncing
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [segmentTab, setSegmentTab] = useState("all"); // pending_vetting | vetting_approved | pending_activation | activated
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showDateFilter, setShowDateFilter] = useState(false);
  
  // Refs for debouncing and request cancellation
  const debounceTimerRef = useRef(null);
  const abortControllerRef = useRef(null);
  const [isSearching, setIsSearching] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    per_page: 10,
    current_page: 1,
    last_page: 1,
    from: 0,
    to: 0,
  });

  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingPromoter, setViewingPromoter] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activatingPromoter, setActivatingPromoter] = useState(null);
  const [showActivationModal, setShowActivationModal] = useState(false);
  const [promoterToActivate, setPromoterToActivate] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [showExportSuccessModal, setShowExportSuccessModal] = useState(false);
  const [exportDetails, setExportDetails] = useState({ segment: "", dateInfo: "" });
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    newToday: 0,
    total_earnings: 0,
    top_performers: 0,
  });

  const segmentTabs = [
    {
      key: "all",
      label: "All",
      Icon: Users,
      activeClasses: "bg-[#FF6C6C] text-white shadow-md shadow-[#FF6C6C]/20",
      iconWrapClasses: "bg-white/15",
    },
    {
      key: "pending_vetting",
      label: "Pending vetting",
      Icon: Shield,
      activeClasses: "bg-[#FF6C6C] text-white shadow-md shadow-[#FF6C6C]/20",
      iconWrapClasses: "bg-white/15",
    },
    {
      key: "vetting_approved",
      label: "Vetted & Approved",
      Icon: Award,
      activeClasses: "bg-[#FF6C6C] text-white shadow-md shadow-[#FF6C6C]/20",
      iconWrapClasses: "bg-white/15",
    },
    {
      key: "rejected",
      label: "Rejected",
      Icon: CircleX,
      activeClasses: "bg-[#FF6C6C] text-white shadow-md shadow-[#FF6C6C]/20",
      iconWrapClasses: "bg-white/15",
    },
    {
      key: "pending_activation",
      label: "Pending activation",
      Icon: Power,
      activeClasses: "bg-[#FF6C6C] text-white shadow-md shadow-[#FF6C6C]/20",
      iconWrapClasses: "bg-white/15",
    },
    {
      key: "activated",
      label: "Activated",
      Icon: UserCheck,
      activeClasses: "bg-[#FF6C6C] text-white shadow-md shadow-[#FF6C6C]/20",
      iconWrapClasses: "bg-white/15",
    },
  ];

  const loadAffiliates = useCallback(async () => {
    // Cancel any pending requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();
    const currentAbortController = abortControllerRef.current;
    
    setIsLoading(true);
    setIsSearching(true);
    
    try {
      // Only fetch stats if not searching (to reduce API calls during search)
      const shouldFetchStats = !searchTerm || searchTerm.trim() === "";
      
      const promises = [
        affiliateService.getAffiliates(
          currentPage,
          itemsPerPage,
          searchTerm,
          sortBy,
          sortOrder,
          dateFrom,
          dateTo,
          "", // channel
          segmentTab
        )
      ];
      
      if (shouldFetchStats) {
        promises.push(affiliateService.getAllAffiliateStats());
      }
      
      const results = await Promise.all(promises);
      const response = results[0];
      const responseStats = results[1];

      // Check if request was aborted
      if (currentAbortController.signal.aborted) {
        return;
      }

      if (shouldFetchStats && responseStats) {
        if(responseStats.data?.status_code === "000"){
          const statsData = responseStats.data.data?.stats || {};
          setStats({
            total: statsData.total || 0,
            active: statsData.active || 0,
            inactive: statsData.inactive || 0,
            newToday: statsData.newToday || 0,
            total_earnings: statsData.total_earnings || 0,
            top_performers: statsData.top_performers || 0,
          });
        } else {
          toast.error(responseStats.message || "Failed to load affiliate stats");
        }
      }
      
      if (response.data?.status_code === "000" && response.data?.data) {
        const mappedPromoters = response.data.data.data.map((affiliate) => ({
          id: affiliate.id,
          first_name: affiliate.first_name,
          last_name: affiliate.last_name,
          account_display_name: affiliate.account_display_name,
          email: affiliate.email,
          phone_number: affiliate.phone_number,
          status: affiliate.affiliate_status || "pending",
          is_active: affiliate.is_active === 1 || affiliate.is_active === true,
          joinDate: affiliate.email_verified_at
            ? affiliate.email_verified_at.split("T")[0]
            : new Date().toISOString().split("T")[0],
          lastActive: affiliate.phone_number_verified_at
            ? affiliate.phone_number_verified_at.split("T")[0]
            : new Date().toISOString().split("T")[0],
          totalEarnings: 0,
          totalPayouts: 0,
          pendingPayouts: 0,
          totalDeals: 0,
          activeDeals: 0,
          conversionRate: 0,
          rating: 0,
          performance: affiliate.affiliate_status === "full_approval" || affiliate.affiliate_status === "limited_approval" ? "good" : "inactive",
          bio: "",
          commissionRate: 10,
          location: affiliate.address,
          verificationStatus: affiliate.email_verified_at
            ? "verified"
            : "pending",
          affiliateCode: affiliate.affiliate_code,
          profileImage: affiliate.profile_image,
          gender: affiliate.gender,
          account_type: affiliate.account_type,
          timezone: affiliate.timezone,
          currency: affiliate.currency,
          company_website: affiliate.company_website,
          linkedin_page: affiliate.linkedin_page,
          website: affiliate.website,
          social: affiliate.social,
          mobile_app: affiliate.mobile_app,
          podcast: affiliate.podcast,
          email_channels: affiliate.email_channels,
          offline: affiliate.offline,
          socialMedia: {
            facebook: affiliate.social?.find(s => s.platform === "Facebook")?.handle || "",
            twitter: affiliate.social?.find(s => s.platform === "Twitter")?.handle || "",
            instagram: affiliate.social?.find(s => s.platform === "Instagram")?.handle || "",
            linkedin: affiliate.linkedin_page || "",
          },
          payoutInfo: {
            network: "",
            phoneNumber: "",
            accountName: "",
          },
        }));

        setPromoters(mappedPromoters);
        
        if (response.data?.data?.pagination) {
          setPagination(response?.data?.data?.pagination);
        }
      } else {
        toast.error(response.message || "Failed to load affiliates");
      }
    } catch (error) {
      // Don't show error if request was aborted
      if (error.name === 'AbortError' || currentAbortController.signal.aborted) {
        return;
      }
      console.error("Error loading affiliates:", error);
      // Only show error toast for non-aborted errors
      if (!currentAbortController.signal.aborted) {
        toast.error("An error occurred while loading affiliates");
      }
    } finally {
      // Only update loading state if this is still the current request
      if (!currentAbortController.signal.aborted) {
        setIsLoading(false);
        setIsSearching(false);
      }
    }
  }, [currentPage, itemsPerPage, searchTerm, sortBy, sortOrder, dateFrom, dateTo, segmentTab]);

  // Handle activation button click - opens modal
  const handleActivateClick = (promoter) => {
    setPromoterToActivate(promoter);
    setShowActivationModal(true);
  };

  // Handle activation/deactivation confirmation
  const handleActivateConfirm = async () => {
    if (!promoterToActivate) return;

    const isCurrentlyActive = promoterToActivate.is_active;
    setActivatingPromoter(promoterToActivate.affiliateCode);
    try {
      const response = await affiliateService.activateAffiliate(
        promoterToActivate.affiliateCode
      );

      if (response.status && response.statusCode === "000") {
        const action = isCurrentlyActive ? "deactivated" : "activated";
        toast.success(`Successfully ${action} ${promoterToActivate.first_name} ${promoterToActivate.last_name}`);
        // Close modal and refresh the list
        setShowActivationModal(false);
        setPromoterToActivate(null);
        await loadAffiliates();
      } else {
        toast.error(response.message || `Failed to ${isCurrentlyActive ? "deactivate" : "activate"} promoter`);
      }
    } catch (error) {
      console.error("Error toggling promoter status:", error);
      toast.error("An error occurred while updating promoter status");
    } finally {
      setActivatingPromoter(null);
    }
  };

  // Handle modal close
  const handleActivationModalClose = () => {
    if (!activatingPromoter) {
      setShowActivationModal(false);
      setPromoterToActivate(null);
    }
  };

  // Handle export
  const handleExport = async () => {
    // Build export description
    const segmentLabel = segmentTabs.find(t => t.key === segmentTab)?.label || "All";
    const dateInfo = dateFrom && dateTo 
      ? `from ${dateFrom} to ${dateTo}` 
      : dateFrom 
        ? `from ${dateFrom}` 
        : dateTo 
          ? `until ${dateTo}` 
          : "all dates";
    
    toast.info(`Processing export for ${segmentLabel} affiliates (${dateInfo})...`, { autoClose: 2500 });
    
    setIsExporting(true);
    try {
      const response = await affiliateService.exportAffiliatesData(
        segmentTab,
        dateFrom,
        dateTo
      );

      if (response.status) {
        setExportDetails({ segment: segmentLabel, dateInfo });
        setShowExportSuccessModal(true);
      } else {
        toast.error(response.message || "Failed to export data. Please try again.");
      }
    } catch (error) {
      console.error("Error exporting data:", error);
      toast.error("An error occurred while exporting data. Please try again.");
    } finally {
      setIsExporting(false);
    }
  };

  // Debounced search effect
  useEffect(() => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const trimmedInput = searchInput.trim();
    
    // If input is cleared, immediately update search term (no debounce needed)
    if (trimmedInput === "" && searchTerm !== "") {
      setSearchTerm("");
      setCurrentPage(1);
      setIsSearching(false);
      return;
    }

    // Show searching indicator if input differs from search term
    if (trimmedInput !== searchTerm) {
      setIsSearching(true);
    }

    // Set new timer for debounced search (only if input is not empty)
    if (trimmedInput !== "") {
      debounceTimerRef.current = setTimeout(() => {
        // Only update search term if it actually changed
        if (trimmedInput !== searchTerm) {
          setSearchTerm(trimmedInput);
          setCurrentPage(1); // Reset to first page on new search
        } else {
          setIsSearching(false);
        }
      }, 500); // 500ms debounce delay
    }

    // Cleanup function
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchInput, searchTerm]); // Depend on both to properly detect changes

  // Load affiliates when filters change (including debounced searchTerm)
  useEffect(() => {
    loadAffiliates();
  }, [loadAffiliates]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Cancel any pending requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      // Clear debounce timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Handle date filter apply
  const handleDateFilterApply = (from, to) => {
    setDateFrom(from);
    setDateTo(to);
    setShowDateFilter(false);
    setCurrentPage(1);
  };

  // Handle date filter clear
  const handleDateFilterClear = () => {
    setDateFrom("");
    setDateTo("");
    setShowDateFilter(false);
    setCurrentPage(1);
  };

  // Reset to page 1 when filters or segment tab change
  useEffect(() => {
    setCurrentPage(1);
  }, [segmentTab, sortBy, sortOrder, dateFrom, dateTo]);

  // Handle clicking outside date filter dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDateFilter && !event.target.closest(".date-filter-container")) {
        setShowDateFilter(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDateFilter]);

  // Use backend pagination - calculate from total and per_page
  const total = pagination.total || 0;
  const perPage = pagination.per_page || itemsPerPage;
  const currentPageNum = pagination.current_page || currentPage;
  const totalPages = Math.ceil(total / perPage) || 1;
  const startIndex = pagination.from || 0;
  const endIndex = pagination.to || 0;
  const paginatedPromoters = promoters;
  
  // Calculate pagination display values
  const showingFrom = startIndex || ((currentPageNum - 1) * perPage + 1);
  const showingTo = endIndex || Math.min(currentPageNum * perPage, total);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "full_approval":
        return "text-green-600 bg-green-100";
      case "limited_approval":
        return "text-blue-600 bg-blue-100";
      case "pending":
        return "text-yellow-600 bg-yellow-100";
      case "rejected":
        return "text-red-600 bg-red-100";
      default:
        return "text-gray-600 bg-gray-100";
    }
  };

  const getStatusLabel = (status) => {
    switch(status) {
      case "full_approval":
        return "Approved";
      case "limited_approval":
        return "Limited";
      case "pending":
        return "Pending";
      case "rejected":
        return "Rejected";
      default:
        return status || "Unknown";
    }
  };



  return (
    <>
      <Navbar page={"Promoters"} />
      <div className="p-6 min-h-screen bg-gray-50">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Affiliate Management
              </h1>
              <p className="mt-2 text-gray-600">
                Manage all affiliate promoters and their activities
              </p>
            </div>
            
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={handleExport}
              disabled={isExporting || isLoading}
              className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-[#FF6C6C] to-[#FF8787] hover:from-[#FF5555] hover:to-[#FF6C6C] text-white font-semibold rounded-xl shadow-lg shadow-[#FF6C6C]/25 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:from-[#FF6C6C] disabled:hover:to-[#FF8787] whitespace-nowrap"
            >
              {isExporting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span className="hidden sm:inline">Exporting...</span>
                  <span className="sm:hidden">Export...</span>
                </>
              ) : (
                <>
                  <Download className="w-5 h-5" />
                  <span className="hidden sm:inline">Export Data</span>
                  <span className="sm:hidden">Export</span>
                </>
              )}
            </motion.button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 gap-6 mb-8 md:grid-cols-2 lg:grid-cols-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="p-6 bg-white rounded-xl border border-gray-100 shadow-sm"
            >
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-600">Total Promoters</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stats.total}
                  </p>
                </div>
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="p-6 bg-white rounded-xl border border-gray-100 shadow-sm"
            >
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-600">Active Promoters</p>
                  <p className="text-2xl font-bold text-green-600">
                    {stats.active}
                  </p>
                </div>
                <div className="p-3 bg-green-100 rounded-lg">
                  <UserCheck className="w-6 h-6 text-green-600" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="p-6 bg-white rounded-xl border border-gray-100 shadow-sm"
            >
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-600">Total Earnings</p>
                  <p className="text-2xl font-bold text-gray-900">
                    GH₵{stats.total_earnings.toLocaleString()}
                  </p>
                </div>
                <div className="p-3 bg-purple-100 rounded-lg">
                  <DollarSign className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="p-6 bg-white rounded-xl border border-gray-100 shadow-sm"
            >
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-sm text-gray-600">Top Performers</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {stats.top_performers}
                  </p>
                </div>
                <div className="p-3 bg-orange-100 rounded-lg">
                  <Award className="w-6 h-6 text-orange-600" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Filters and Actions */}
        <div className="p-6 mb-6 bg-white rounded-xl border border-gray-100 shadow-sm">
          {/* Segment tabs – full width, top */}
          <div className="pb-4 mb-5 border-b border-gray-100">
            <p className="mb-3 text-xs font-semibold tracking-wider text-gray-400 uppercase">Segment</p>
            <div className="flex flex-wrap gap-2">
              {segmentTabs.map(({ key, label, Icon, activeClasses, iconWrapClasses }) => {
                const isActive = segmentTab === key;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setSegmentTab(key)}
                    className={[
                      "group inline-flex items-center gap-2 px-3.5 py-2.5 rounded-full border transition-all duration-200",
                      "text-sm font-semibold",
                      isActive
                        ? `${activeClasses} border-transparent`
                        : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-white hover:border-gray-300 hover:shadow-sm",
                    ].join(" ")}
                  >
                    <span
                      className={[
                        "inline-flex items-center justify-center w-8 h-8 rounded-full transition-colors",
                        isActive ? iconWrapClasses : "bg-white border border-gray-200 group-hover:border-gray-300",
                      ].join(" ")}
                    >
                      <Icon className={isActive ? "w-4 h-4 text-white" : "w-4 h-4 text-[#FF6C6C]"} />
                    </span>
                    <span className="whitespace-nowrap">{label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Single row: search, date, sort, per page – aligned */}
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
            <div className="flex flex-col flex-1 gap-3 min-w-0 sm:flex-row sm:items-center">
              <div className="relative flex-1 min-w-0 sm:max-w-[280px]">
                <Search className={`absolute left-3 top-1/2 w-5 h-5 -translate-y-1/2 ${
                  isSearching ? "text-[#FF6C6C] animate-pulse" : "text-gray-400"
                }`} />
                <input
                  type="text"
                  placeholder="Search promoters..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] transition-all duration-200 outline-none text-sm"
                />
                {searchInput && searchInput !== searchTerm && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <Loader2 className="w-4 h-4 text-[#FF6C6C] animate-spin" />
                  </div>
                )}
              </div>

              <div className="flex relative gap-3 items-center date-filter-container">
                <button
                  onClick={() => setShowDateFilter(!showDateFilter)}
                  className={`inline-flex items-center gap-2 px-3 py-2.5 border rounded-lg text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                    dateFrom || dateTo
                      ? "border-[#FF6C6C] bg-[#FF6C6C]/5 text-[#FF6C6C]"
                      : "border-gray-300 hover:border-[#FF6C6C] hover:bg-gray-50 text-gray-700"
                  }`}
                >
                  <Calendar className="w-4 h-4 shrink-0" />
                  {dateFrom || dateTo ? "Date filtered" : "Filter by date"}
                  {(dateFrom || dateTo) && (
                    <X
                      size={14}
                      className="shrink-0 hover:bg-[#FF6C6C]/10 rounded-full p-0.5"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDateFilterClear();
                      }}
                    />
                  )}
                </button>

                {showDateFilter && (
                  <div className="absolute left-0 top-full z-50 p-4 mt-2 w-full bg-white rounded-xl border border-gray-200 shadow-lg sm:w-80 date-filter-container">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-sm font-semibold text-gray-900">Filter by date range</h3>
                      <button onClick={() => setShowDateFilter(false)} className="text-gray-400 hover:text-gray-600">
                        <X size={16} />
                      </button>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <label className="block mb-1 text-sm font-medium text-gray-700">From</label>
                        <input
                          type="date"
                          id="dateFrom"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] outline-none text-sm"
                          max={dateTo || undefined}
                          defaultValue={dateFrom}
                        />
                      </div>
                      <div>
                        <label className="block mb-1 text-sm font-medium text-gray-700">To</label>
                        <input
                          type="date"
                          id="dateTo"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] outline-none text-sm"
                          min={dateFrom || undefined}
                          defaultValue={dateTo}
                        />
                      </div>
                    </div>
                    <div className="flex justify-between items-center pt-4 mt-4 border-t border-gray-200">
                      <button onClick={handleDateFilterClear} className="text-sm text-gray-600 hover:text-gray-800">
                        Clear
                      </button>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setShowDateFilter(false)}
                          className="px-3 py-2 text-sm text-gray-600 rounded-lg border border-gray-300 hover:bg-gray-50"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => {
                            const fromDate = document.getElementById("dateFrom").value;
                            const toDate = document.getElementById("dateTo").value;
                            handleDateFilterApply(fromDate, toDate);
                          }}
                          className="px-3 py-2 text-sm text-white bg-[#FF6C6C] rounded-lg hover:bg-[#FF6C6C]/90"
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-3 items-center lg:gap-4 lg:ml-auto shrink-0">
              <div className="flex gap-2 items-center">
                <label className="text-sm text-gray-500 whitespace-nowrap">Sort</label>
                <div className="relative">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="pl-3 pr-9 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] outline-none bg-white appearance-none"
                  >
                    <option value="first_name">Name</option>
                    <option value="earnings">Earnings</option>
                    <option value="joinDate">Join date</option>
                    <option value="status">Status</option>
                  </select>
                  <ChevronDown className="absolute right-2.5 top-1/2 w-4 h-4 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
              </div>
              <button
                onClick={() => setSortOrder(sortOrder === "desc" ? "asc" : "desc")}
                className="inline-flex items-center gap-1.5 px-3 py-2.5 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm font-medium transition-colors"
                title={sortOrder === "desc" ? "Ascending" : "Descending"}
              >
                {sortOrder === "desc" ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
                <span>{sortOrder === "desc" ? "Desc" : "Asc"}</span>
              </button>
              <div className="flex gap-2 items-center">
                <label className="text-sm text-gray-500 whitespace-nowrap">Show</label>
                <div className="relative">
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="pl-3 pr-9 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] outline-none bg-white appearance-none"
                  >
                    <option value={15}>15</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <ChevronDown className="absolute right-2.5 top-1/2 w-4 h-4 -translate-y-1/2 text-gray-400 pointer-events-none" />
                </div>
                <span className="text-sm text-gray-500">per page</span>
              </div>
            </div>
          </div>
        </div>

        {/* Promoters Table */}
        <div className="overflow-hidden bg-white rounded-xl border border-gray-100 shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                    Promoter
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                    Earnings
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                    Join Date
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">
                    Last Active
                  </th>
                  <th className="px-6 py-3 text-xs font-medium tracking-wider text-right text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {isLoading ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-12 text-center">
                      <div className="flex justify-center items-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6C6C]"></div>
                      </div>
                    </td>
                  </tr>
                ) : paginatedPromoters.length === 0 ? (
                  <tr>
                    <td
                      colSpan="6"
                      className="px-6 py-12 text-center text-gray-500"
                    >
                      No promoters found matching your criteria
                    </td>
                  </tr>
                ) : (
                  paginatedPromoters.map((promoter) => (
                    <motion.tr
                      key={promoter.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="transition-colors hover:bg-gray-50"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 w-10 h-10">
                            {promoter.profileImage ? (
                              <img
                                src={promoter.profileImage}
                                alt={`${promoter.first_name} ${promoter.last_name}`}
                                className="object-cover w-10 h-10 rounded-full"
                              />
                            ) : (
                              <div className="h-10 w-10 rounded-full bg-gradient-to-r from-[#FF6C6C] to-[#FF9B9B] flex items-center justify-center">
                                <span className="text-sm font-semibold text-white">
                                  {promoter.first_name?.charAt(0) || "?"}
                                </span>
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {promoter.first_name} {promoter.last_name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {promoter.account_display_name && (
                                <span className="text-xs text-blue-600">
                                  @{promoter.account_display_name}
                                </span>
                              )}
                            </div>
                            <div className="text-sm text-gray-500">
                              {promoter.email}
                            </div>
                            <div className="text-xs text-gray-400">
                              {promoter.phone_number} • {promoter.affiliateCode}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${getStatusColor(
                            promoter.status
                          )}`}
                        >
                          {getStatusLabel(promoter.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          <div className="font-medium">
                            GH₵{promoter.totalEarnings.toLocaleString()}
                          </div>
                          <div className="text-xs text-gray-500">
                            Pending: GH₵
                            {promoter.pendingPayouts.toLocaleString()}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {new Date(promoter.joinDate).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {new Date(promoter.lastActive).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-sm font-medium text-right">
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => {
                              setViewingPromoter(promoter);
                              setShowViewModal(true);
                            }}
                            className="p-2 text-[#FF6C6C] rounded-lg transition-colors hover:bg-[#FF6C6C]/10"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => navigate(`/promoters/${promoter.affiliateCode}/approve`)}
                            className="p-2 text-blue-600 rounded-lg transition-colors hover:bg-blue-50"
                            title="Vet Affiliate"
                          >
                            <Shield className="w-4 h-4" />
                          </button>
                          {/* Toggle Activation button - show for all approved promoters */}
                          {/* {(promoter.status === "full_approval" || promoter.status === "limited_approval") && (
                            <button
                              onClick={() => handleActivateClick(promoter)}
                              disabled={activatingPromoter === promoter.affiliateCode}
                              className={`p-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                                (promoter.is_active === 1 || promoter.is_active === true)
                                  ? "text-red-600 hover:bg-red-50"
                                  : "text-green-600 hover:bg-green-50"
                              }`}
                              title={(promoter.is_active === 1 || promoter.is_active === true) ? "Deactivate Publisher" : "Activate Publisher"}
                            >
                              {activatingPromoter === promoter.affiliateCode ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Power className="w-4 h-4" />
                              )}
                            </button>
                          )} */}
                        </div>
                      </td>
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {promoters.length > 0 && totalPages > 1 && (
            <div className="flex flex-col gap-4 px-6 py-4 border-t border-gray-200 sm:flex-row sm:justify-between sm:items-center">

              <div className="flex gap-2 items-center">
                <span className="text-sm text-gray-600">
                  Showing {showingFrom} to {showingTo} of{" "}
                  {total} entries
                </span>
              </div>

              <div className="flex gap-2 items-center">
                <button
                  onClick={() => handlePageChange(currentPageNum - 1)}
                  disabled={currentPageNum === 1}
                  className={`p-2 rounded-lg transition-colors ${
                    currentPageNum === 1
                      ? "text-gray-300 cursor-not-allowed"
                      : "text-gray-600 hover:bg-gray-100 hover:text-[#FF6C6C]"
                  }`}
                  title="Previous page"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>

                <div className="flex gap-1 items-center">
                  {Array.from(
                    { length: Math.min(5, totalPages) },
                    (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPageNum <= 3) {
                        pageNum = i + 1;
                      } else if (currentPageNum >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPageNum - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                            currentPageNum === pageNum
                              ? "bg-[#FF6C6C] text-white"
                              : "text-gray-600 hover:bg-gray-100"
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    }
                  )}
                </div>

                <button
                  onClick={() => handlePageChange(currentPageNum + 1)}
                  disabled={currentPageNum === totalPages}
                  className={`p-2 rounded-lg transition-colors ${
                    currentPageNum === totalPages
                      ? "text-gray-300 cursor-not-allowed"
                      : "text-gray-600 hover:bg-gray-100 hover:text-[#FF6C6C]"
                  }`}
                  title="Next page"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Activation Confirmation Modal */}
        <ActivationConfirmationModal
          isOpen={showActivationModal}
          onClose={handleActivationModalClose}
          onConfirm={handleActivateConfirm}
          promoterName={promoterToActivate ? `${promoterToActivate.first_name} ${promoterToActivate.last_name}` : ""}
          isActivating={!!activatingPromoter}
          isActive={promoterToActivate?.is_active || false}
        />

        {/* View Promoter Modal */}
        {showViewModal && viewingPromoter && (
          <ViewPromoterModal
            promoter={viewingPromoter}
            onClose={() => {
              setShowViewModal(false);
              setViewingPromoter(null);
            }}
            onRefresh={async () => {
              try {
                const response = await affiliateService.showAffiliateDetails(
                  viewingPromoter.affiliateCode
                );
                if (response.status && response.data?.status_code === "000") {
                  const updatedPromoter = {
                    ...viewingPromoter,
                    first_name: response.data.data.first_name,
                    last_name: response.data.data.last_name,
                    email: response.data.data.email,
                    phone_number: response.data.data.phone_number,
                    location: response.data.data.address,
                    gender: response.data.data.gender,
                    profileImage: response.data.data.profile_image,
                    verificationStatus: response.data.data.email_verified_at
                      ? "verified"
                      : "pending",
                    status: response.data.data.affiliate_status || "pending",
                  };
                  setViewingPromoter(updatedPromoter);
                }
              } catch (error) {
                console.error("Error refreshing promoter details:", error);
                toast.error("Failed to refresh promoter details");
              }
            }}
          />
        )}

        {/* Export Success Modal */}
        {showExportSuccessModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowExportSuccessModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Success Header with Gradient */}
              <div className="relative px-6 pt-8 pb-6 bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500" />
                <button
                  onClick={() => setShowExportSuccessModal(false)}
                  className="absolute top-4 right-4 p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-white/60 transition-all"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
                
                {/* Success Icon */}
                <div className="flex justify-center mb-4">
                  <div className="relative">
                    <div className="absolute inset-0 bg-green-500/20 rounded-full blur-xl animate-pulse" />
                    <div className="relative flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 shadow-lg">
                      <CheckCircle className="w-10 h-10 text-white" strokeWidth={2.5} />
                    </div>
                  </div>
                </div>

                {/* Success Title */}
                <h2 className="text-2xl font-bold text-center text-gray-900 mb-2">
                  Export Successful!
                </h2>
                <p className="text-center text-gray-600 text-sm">
                  Your data is being prepared
                </p>
              </div>

              {/* Modal Body */}
              <div className="px-6 py-6 space-y-4">
                {/* Email Notice */}
                <div className="flex items-start gap-4 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 shadow-sm">
                      <Mail className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-gray-900 mb-1">
                      Check Your Email
                    </p>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      The Excel file will be <span className="font-semibold text-gray-900">sent to your email</span> within the next few minutes.
                    </p>
                  </div>
                </div>

                {/* Export Details */}
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-100 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Segment:</span>
                    <span className="font-semibold text-gray-900">{exportDetails.segment}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Date Range:</span>
                    <span className="font-medium text-gray-700 capitalize">{exportDetails.dateInfo}</span>
                  </div>
                </div>

                {/* Action Button */}
                <button
                  onClick={() => setShowExportSuccessModal(false)}
                  className="w-full py-3 px-4 bg-gradient-to-r from-[#FF6C6C] to-[#FF8787] hover:from-[#FF5555] hover:to-[#FF6C6C] text-white font-semibold rounded-xl shadow-lg shadow-[#FF6C6C]/25 transition-all duration-200"
                >
                  Got it!
                </button>
              </div>
            </motion.div>
          </div>
        )}

      </div>
    </>
  );
};

export default Promoters;
